import { FC, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import { useSuspenseQuery } from '@tanstack/react-query'

import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Grid,
  MultiSelect,
  Select,
  Separator,
  Stack,
  TextField,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core/common'

import { languages, useAuth } from '@/features/auth'
import { useLocalisation } from '@/utils/localisation'

import { CreateUserFormSchema, CreateUserFormSchemaType } from './schema'
import { organizationsQueryOptions } from '../api/master-data.service'
import { useUserCreate } from '../api/useUserCreate'
import { isValidSalutation, roles, Salutation, salutations, UserRole } from '../domain/user'

export type CreateUserDialogProps = {
  onClose?: () => void
}

export const CreateUserDialog: FC<CreateUserDialogProps> = ({ onClose }) => {
  const { customer } = useAuth()
  const { translate } = useLocalisation()
  const toaster = useToaster()
  const userMutation = useUserCreate()
  const { data: organizations } = useSuspenseQuery(organizationsQueryOptions(customer?._id || ''))
  const [error, setError] = useState<string | undefined>(undefined)
  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateUserFormSchema),
    defaultValues: {
      salutation: undefined as Salutation | undefined,
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneLandline: '',
      phoneMobile: '',
      languages: [],
      roles: ['manager'] as UserRole[],
      organizationId: '',
    },
  })
  const onSubmit: SubmitHandler<CreateUserFormSchemaType> = async (data) => {
    setError(undefined)
    try {
      await userMutation.mutateAsync({
        data,
      })
      toaster({
        title: translate('users.create.notifications.created', {
          firstName: data.firstName,
          lastName: data.lastName,
        }),
      })
      onClose?.()
    } catch (e) {
      if (e instanceof Error && e.message) {
        setError(e.message)
      }
    }
  }

  return (
    <Dialog
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose?.()
        }
      }}
    >
      <DialogContent maxHeight="full">
        <DialogHeader>
          <DialogTitle>{translate('users.create.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={16}>
            {error ? <Alert title={error} /> : null}

            <Typography variant="body-bold">{translate('users.create.sections.personal.title')}</Typography>

            <Grid col={2} gap={16}>
              <Select
                label={translate('users.create.fields.salutation.label')}
                placeholder={translate('users.create.fields.salutation.placeholder')}
                value={getValues('salutation')}
                onChange={(newSalutation) => {
                  if (isValidSalutation(newSalutation)) {
                    setValue('salutation', newSalutation, { shouldValidate: true })
                  }
                }}
                state={errors.salutation ? 'negative' : undefined}
                items={salutations.map((salutation) => ({
                  value: salutation,
                  label: translate(`salutations.${salutation}`),
                }))}
              />

              <TextField
                label={translate('users.create.fields.title.label')}
                placeholder={translate('users.create.fields.title.placeholder')}
                {...register('title')}
                state={errors.title ? 'negative' : undefined}
              />

              <TextField
                label={translate('users.create.fields.first_name.label')}
                placeholder={translate('users.create.fields.first_name.placeholder')}
                required
                {...register('firstName')}
                state={errors.firstName ? 'negative' : undefined}
              />

              <TextField
                label={translate('users.create.fields.last_name.label')}
                placeholder={translate('users.create.fields.last_name.placeholder')}
                required
                {...register('lastName')}
                state={errors.lastName ? 'negative' : undefined}
              />

              <TextField
                label={translate('users.create.fields.phone_landline.label')}
                placeholder={translate('users.create.fields.phone_landline.placeholder')}
                {...register('phoneLandline')}
                state={errors.phoneLandline ? 'negative' : undefined}
              />

              <TextField
                label={translate('users.create.fields.phone_mobile.label')}
                placeholder={translate('users.create.fields.phone_mobile.placeholder')}
                {...register('phoneMobile')}
                state={errors.phoneMobile ? 'negative' : undefined}
              />

              <TextField
                type="email"
                label={translate('users.create.fields.email.label')}
                placeholder={translate('users.create.fields.email.placeholder')}
                required
                {...register('email')}
                state={errors.email ? 'negative' : undefined}
              />
            </Grid>

            <Controller
              control={control}
              name="languages"
              render={({ field: { onChange } }) => (
                <MultiSelect
                  items={languages.map((language) => ({
                    label: translate(`languages.${language}`),
                    value: language,
                  }))}
                  label={translate('users.create.fields.languages.label')}
                  placeholder={translate('users.create.fields.languages.placeholder')}
                  value={getValues('languages')}
                  onChange={(newLanguages) => {
                    onChange(newLanguages)
                  }}
                  state={errors.languages ? 'negative' : undefined}
                />
              )}
            />

            <Separator orientation="horizontal" />

            <Typography variant="body-bold">{translate('users.create.sections.management.title')}</Typography>

            <Grid col={2} gap={16}>
              <Controller
                control={control}
                name="roles"
                render={({ field: { onChange } }) => (
                  <MultiSelect
                    items={roles.map((role) => ({
                      label: translate(`users.roles.${role}`),
                      value: role,
                    }))}
                    label={translate('users.create.fields.roles.label')}
                    value={getValues('roles')}
                    onChange={(newRoles) => {
                      onChange(newRoles)
                    }}
                    state={errors.roles ? 'negative' : undefined}
                  />
                )}
              />

              <Select
                required
                label={translate('users.create.fields.organization.label')}
                placeholder={translate('users.create.fields.organization.placeholder')}
                value={getValues('organizationId')}
                onChange={(newOrganization) => {
                  setValue('organizationId', newOrganization, { shouldValidate: true })
                }}
                state={errors.organizationId ? 'negative' : undefined}
                items={organizations.data.map((organization) => ({
                  value: organization.id,
                  label: organization.name,
                }))}
              />
            </Grid>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline-primary"
            onClick={() => {
              onClose?.()
            }}
          >
            {translate('users.create.actions.cancel')}
          </Button>
          <Button disabled={userMutation.isPending} onClick={handleSubmit(onSubmit)}>
            {translate('users.create.actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
